import { HTMLInputTypeAttribute } from 'react';
import { Control, Controller, FieldErrors, FieldPath, FieldValues, get } from 'react-hook-form';

import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';

export type FormInputTextProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  className?: string;
  inputClassName?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  field: TName;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  type?: HTMLInputTypeAttribute | undefined;
  required?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
};

const FormInputText = <TFieldValues extends FieldValues>({ ...props }: FormInputTextProps<TFieldValues>): JSX.Element => {
  const {
    className,
    inputClassName,
    control,
    field,
    errors,
    label,
    disabled,
    placeholder,
    type,
    required,
    autoFocus,
    maxLength,
  } = props;

  const errorTemplate = () => {
    const error = get(errors, field);
    return error && <small className="p-error">{error?.message as string}</small>;
  };

  return (
    <div className={className ?? 'field'}>
      <Controller
        name={field}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <label htmlFor={field.name} className={classNames('font-medium', { 'p-error': fieldState.error, disabled })}>
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <InputText
              id={field.name}
              value={field.value ?? ''}
              onChange={(e) => (e.target.value ? field.onChange(e.target.value as any) : field.onChange(null as any))}
              className={classNames(inputClassName, { 'p-invalid': fieldState.error, disabled })}
              disabled={disabled}
              placeholder={placeholder}
              type={type}
              autoFocus={autoFocus}
              maxLength={maxLength}
            />
          </>
        )}
      />
      {errorTemplate()}
    </div>
  );
};

export default FormInputText;
